import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../services/useAuth";
import { hasPageAccess } from "../config/roles";

export const useAuthCheck = (requiredRoles = []) => {
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, getToken } = useAuth();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Token kontrolü
        const token = getToken();
        if (!token) {
          navigate("/login", { replace: true });
          return;
        }

        // Kullanıcı kontrolü
        if (!user) {
          navigate("/login", { replace: true });
          return;
        }

        const userRole = user.role?.toLowerCase();
        const userModules = user.modules || [];
        const currentPath = location.pathname;
        console.log(userRole, userModules, currentPath);

        // Special pages that require admin access
        const isUsersPage =
          currentPath.includes("/users") || currentPath.includes("/user");
        const isMQTTPage = currentPath.includes("/mqtt");

        // Admin or Tam Erişim — profile-only pages (e.g. EPC) still need explicit grant for admin only
        const isElevated = userRole === "admin" || user.fullAccess;
        if (isElevated) {
          if ((isUsersPage || isMQTTPage) && requiredRoles.length > 0) {
            if (!requiredRoles.includes("admin")) {
              navigate("/404", { replace: true });
              return;
            }
          }

          const hasAccess = hasPageAccess(
            userRole,
            userModules,
            currentPath,
            user.pages || [],
            !!user.fullAccess,
          );
          if (!hasAccess) {
            navigate("/404", { replace: true });
            return;
          }

          setIsAuthorized(true);
          setIsLoading(false);
          return;
        }

        // Check module-based page access for company, employee, and user roles
        if (userRole === "company" || userRole === "employee" || userRole === "user") {
          const userPages = user.pages || [];
          // Use hasPageAccess to check if user has module/page access to current page
          const hasAccess = hasPageAccess(
            userRole,
            userModules,
            currentPath,
            userPages,
            !!user.fullAccess,
          );

          if (!hasAccess) {
            navigate("/404", { replace: true });
            return;
          }

          // If requiredRoles is specified, also check role requirements
          if (requiredRoles.length > 0) {
            // Check if user has one of the required roles
            let hasRequiredRole = requiredRoles.includes(userRole);
            
            // Company role implies employee/user access levels
            if (userRole === "company" && (requiredRoles.includes("employee") || requiredRoles.includes("user"))) {
              hasRequiredRole = true;
            }

            if (!hasRequiredRole) {
              navigate("/404", { replace: true });
              return;
            }
          }

          setIsAuthorized(true);
          setIsLoading(false);
          return;
        }

        // Legacy role checks for backward compatibility (company, dealer, etc.)
        if (requiredRoles.length > 0) {
          let hasRequiredRole = false;

          // Check direct role match
          if (requiredRoles.includes(userRole)) {
            hasRequiredRole = true;
          }

          // Additional checks for legacy roles
          if (!hasRequiredRole) {
            // Users page requires admin
            if (isUsersPage) {
              if (user.isAdmin === true) {
                if (user.companyId && requiredRoles.includes("company")) {
                  hasRequiredRole = true;
                }
                if (user.dealerId && requiredRoles.includes("dealer")) {
                  hasRequiredRole = true;
                }
                const userAssignedType = user.assignedType?.toLowerCase();
                if (
                  userAssignedType &&
                  requiredRoles.includes(userAssignedType)
                ) {
                  hasRequiredRole = true;
                }
              }
            } else {
              // More permissive checks for other pages
              if (user.companyId && requiredRoles.includes("company")) {
                hasRequiredRole = true;
              }
              if (user.dealerId && requiredRoles.includes("dealer")) {
                hasRequiredRole = true;
              }
              const userAssignedType = user.assignedType?.toLowerCase();
              if (
                userAssignedType &&
                requiredRoles.includes(userAssignedType)
              ) {
                hasRequiredRole = true;
              }
              if (
                requiredRoles.includes("admin") &&
                (user.isAdmin === true || userRole === "admin")
              ) {
                hasRequiredRole = true;
              }
            }
          }

          if (!hasRequiredRole) {
            navigate("/404", { replace: true });
            return;
          }
        } else {
          // No required roles specified - check module access for company/employee/user
          if (userRole === "company" || userRole === "employee" || userRole === "user") {
            const hasAccess = hasPageAccess(
              userRole,
              userModules,
              currentPath,
              user.pages || [],
              !!user.fullAccess,
            );
            if (!hasAccess) {
              navigate("/404", { replace: true });
              return;
            }
          }
        }

        setIsAuthorized(true);
      } catch (error) {
        console.error("Auth check error:", error);
        navigate("/login", { replace: true });
      } finally {
        setIsLoading(false);
      }
    };

    // Kullanıcı bilgisi, token veya pathname değiştiğinde kontrol et
    if (user !== null || getToken() === null) {
      checkAuth();
    }
  }, [
    requiredRoles,
    navigate,
    user,
    user?.role,
    user?.modules,
    user?.pages,
    user?.fullAccess,
    getToken,
    location.pathname,
  ]);

  return { isAuthorized, isLoading };
};
